import { useState } from "react"
import { CellContext } from "@tanstack/react-table"

import { FeatureFlag, isFeatureEnabled } from "store/featureFlagsSlice"
import { useAppSelector } from "store/hooks"
import { ApiTypes, UITypes } from "types"
import { CellComponentProps } from "types/table/cellComponentProps"

import { getProductTypeLabel } from "utils/getProductTypeLabel"
import { isSerializedProductType } from "utils/productType"
import { OrderItemsModal } from "pages/contractor/Orders/OrderItemsModal"

import { groupByProductType, QuantityChips } from "./QuantityChips"

interface OrdersQuantityProps {
  cell: CellContext<UITypes.TableBodyRow, UITypes.TableBodyCell>
  cellComponentProps?: CellComponentProps
}

const OrdersQuantity = ({ cell, cellComponentProps }: OrdersQuantityProps) => {
  const items = cell.row.original.rowSettings.item as ApiTypes.Model.OrderNewItem[] | undefined
  const isVendor = cellComponentProps?.isVendor
  const isWHManager = cellComponentProps?.isWHManager
  const showV2 = useAppSelector(isFeatureEnabled(FeatureFlag.ThermV2Ordering))
  const [showOrderItemsModal, setShowOrderItemsModal] = useState(false)

  if (!Array.isArray(items) || items.length === 0) return null

  const orderItem = items[0]
  const orderNumber = orderItem.id
  const showClickableQuantity = !!cell.row.parentId || cell.row.original.subRows?.length === 0
  const isExpanded = cell.row.getIsExpanded()
  if (isExpanded && items.length > 1) return null

  const showDetails =
    ["packed", "shipping", "archived"].includes(orderItem.status) && isSerializedProductType(orderItem.product_type)
  const isPackedStatus = orderItem.status === "packed"
  const isClickable = showDetails && showClickableQuantity

  const isContractorContext = !isVendor && !isWHManager
  const useContractorOverride = isContractorContext && !showV2

  const chipItems = groupByProductType(items).map(({ productType, quantity }) => ({
    productType,
    quantity,
    label: getProductTypeLabel(productType, useContractorOverride),
  }))

  if (chipItems.length === 0) return null

  return (
    <>
      <QuantityChips
        items={chipItems}
        idSeed={orderNumber}
        onChipClick={isClickable ? () => setShowOrderItemsModal(true) : undefined}
        clickTooltipText="See the list of devices"
      />
      {showOrderItemsModal && (
        <OrderItemsModal
          orderNumber={orderNumber}
          isVendor={!!isVendor}
          isWHManager={!!isWHManager}
          isPackedStatus={isPackedStatus}
          quantity={orderItem.quantity}
          productType={orderItem.product_type}
          onClose={() => setShowOrderItemsModal(false)}
        />
      )}
    </>
  )
}

export default OrdersQuantity
